/**
 * A LINE-BASED, 1-based INCLUSIVE foldable range in a document. The editor folds
 * from the end of `startLine` to the end of `endLine`, so the first line stays
 * visible (a heading / signature line). Only foldable when `endLine > startLine`.
 *
 * Pure data: no DOM, no CodeMirror. The editor (in @slate/ui) converts these to
 * offsets; @slate/core only ever produces or passes the line numbers.
 */
export interface FoldRange {
  /** 1-based source line where the fold begins (stays visible). */
  startLine: number;
  /** 1-based source line where the fold ends (inclusive). */
  endLine: number;
}

/**
 * Produces foldable ranges for one or more file extensions. Built-ins register an
 * in-process provider; third-party plugins register one backed by their worker.
 * Mirrors `OutlineProvider`, except a file can have SEVERAL applicable providers
 * at once (e.g. markdown headings AND an indentation fallback), so the registry
 * returns an array.
 */
export interface FoldProvider {
  /** Stable id (built-in name or plugin id). */
  id: string;
  /** Lowercase extensions (no dot) this provider folds, e.g. ['md', 'markdown']. */
  extensions: readonly string[];
  /** Produce fold ranges for `source`. `fileName` lets a provider pick a dialect. */
  getFolds(source: string, fileName: string): Promise<FoldRange[]>;
}

export interface FoldRegistry {
  /** Register a provider; returns a disposer that removes it. */
  register(p: FoldProvider): () => void;
  /** EVERY provider matching a file by extension, newest registration first. */
  providersForFile(fileName: string): FoldProvider[];
  /** Subscribe to registry changes (register / dispose). */
  subscribe(cb: () => void): () => void;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

export function createFoldRegistry(): FoldRegistry {
  // Newest first, so a later registration (e.g. a plugin override) is preferred
  // when the app dedupes overlapping ranges by start line.
  const providers: FoldProvider[] = [];
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((l) => l());

  return {
    register(p: FoldProvider): () => void {
      providers.unshift(p);
      emit();
      return () => {
        const i = providers.indexOf(p);
        if (i >= 0) {
          providers.splice(i, 1);
          emit();
        }
      };
    },
    providersForFile(fileName: string): FoldProvider[] {
      const ext = extOf(fileName);
      if (ext === '') return [];
      return providers.filter((p) => p.extensions.includes(ext));
    },
    subscribe(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

/** The shared registry instance the app resolves fold ranges through. */
export const foldRegistry: FoldRegistry = createFoldRegistry();
